<template>
  <a-button
    ref="googleLoginButton"
    size="large"
    :disabled="!client || disabled"
    class="google-login-button yy-btn-xlg btn-shadow"
    @click="handleClick"
  >
    <Resource
      class="w-[24px] h-[24px] icon-google"
      :src="getFeUrl('/youyan-platform/png/icon-google.png')"
      :transform-resource="transformImage"
    ></Resource>
    {{ $t('continue-with-google') }}
  </a-button>
</template>

<script lang="ts" setup>
import { omit } from 'lodash-es'
import {
  getUserInternationalLoginAuthorize,
  postUserInternationalOauth2Login,
} from '~/api'
import { getLocation } from '~/api/module/seo'
import { INVITE_CODE, SESSION_REF, SESSION_SOURCE } from '~/constants/config'
import { transformImage } from '~/utils/'

defineProps<{ disabled?: boolean }>()
const emits = defineEmits(['success'])

const googleLoginButton = ref<HTMLElement | null>(null)
const route = useRoute()
const client = ref<any>()

const handleClick = async () => {
  trackEvent(TRACK_EVENT.LOGIN_GOOGLE_CLICK)
  await initialize()
  if (client.value) {
    client.value.requestCode()
  }
}

const loadScript = () => {
  return new Promise<void>((resolve) => {
    if (window.google) {
      return resolve()
    }
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.onload = () => resolve()
    document.body.appendChild(script)
  })
}

const initialize = async () => {
  const [locationRes, googleOauthRes] = await concurrency([
    getLocation(),
    getUserInternationalLoginAuthorize({
      service: 'google',
    }),
    loadScript(),
  ])

  if (googleOauthRes) {
    client.value = window.google.accounts.oauth2.initCodeClient({
      ...omit(googleOauthRes.data, 'nonce'),
      callback: async (revocationResponse: any) => {
        const source = sessionGetItem(SESSION_SOURCE) || undefined // 第三方来源注册
        const linkCode = sessionGetItem(INVITE_CODE) || undefined // 邀请码注册

        const utm_source = route.query?.utm_source as string
        const utm_medium = route.query?.utm_medium as string
        const utm_campaign = route.query?.utm_campaign as string
        const utm_content = route.query?.utm_content as string
        const utm_term =
          route.query?.utm_term || (sessionGetItem(SESSION_REF) as string)
        const keyword_search = route.query?.keyword_search as string
        const gclid = route.query?.gclid as string

        try {
          const res = await postUserInternationalOauth2Login({
            code: revocationResponse.code,
            state: revocationResponse.state,
            service: 'google',
            link_code: linkCode || undefined,
            source,
            utm_campaign,
            utm_content,
            utm_medium,
            utm_source,
            utm_term,
            keyword_search,
            gclid,
            country: locationRes.country,
          })
          trackEvent(TRACK_EVENT.GOOGLE_LOGIN_SUCCESS)
          emits('success', res.data)
        } catch (err) {}
      },
    })
  }
}

onMounted(initialize)
</script>

<style lang="scss" scoped>
.google-login-button {
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.icon-google {
  display: inline-block;
  margin-right: 6px;
}
</style>
